import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  type PaginationState,
  useReactTable,
} from '@tanstack/react-table'
import { useMemo, useState } from 'react'

import TanstackTable from 'components/reusable/table/TanstackTable'
import { type ChartConfig } from 'components/ui/chart'

import NeedsAttentionPieChart from './NeedsAttentionPieChart'
import { type NAVStatisticsProps, type StatisticsType } from './types'
import { toSnakeCase } from './utils'
import { colors } from '../../../../../config/tailwind/colors'

const chartColors = [
  colors.primitive.thatch['800'],
  colors.primitive.thatch['700'],
  colors.primitive.thatch['600'],
  colors.primitive.thatch['500'],
  colors.primitive.thatch['400'],
  colors.primitive.thatch['300'],
  colors.primitive.thatch['200'],
  colors.primitive.thatch['100'],
]

const baseChartConfig: ChartConfig = {
  carrier: {
    label: 'Carrier',
  },
  items: {
    label: 'NAV Items',
  },
  other: {
    label: 'Other',
    color: chartColors[chartColors.length - 1],
  }
}

// For most charts we have a specific number of possible items + colors to display, but for carrier
// names we don't know how many there will be, so we need to dynamically create the chart config.
// This function will group the carrier names into a single "Other" category if there are more than 7
// unique carrier names. The top 7 will be displayed with their own colors, and the rest will be
// grouped into the "Other" category.
function getPieChartDataFromStatistics (
  byCarrierStats: Record<string, number> | undefined
): StatisticsType[] {
  if (!byCarrierStats) return []

  const entries = Object.entries(byCarrierStats)

  const sorted = entries.sort((a, b) => b[1] - a[1])
  const top7 = sorted.slice(0, 7)
  const rest = sorted.slice(7)

  const otherTotal = rest.reduce((sum, [, count]) => sum + count, 0)

  const formattedTop7: StatisticsType[] = top7.map(([key, value]) => ({
    carrier: toSnakeCase(key),
    items: value,
  }))

  if (otherTotal > 0) {
    formattedTop7.push({
      carrier: 'other',
      items: otherTotal,
    })
  }

  return formattedTop7
}

function getTableChartDataFromStatistics (
  byCarrierStats: Record<string, number> | undefined
): StatisticsType[] {
  return Object.entries(byCarrierStats || {})
    .map(([key, value]) => ({
      carrier: key,
      items: value,
    }))
    .sort((a, b) => b.items - a.items)
}

export const NAVCarrierNamePieChart = ({
  stats: byCarrierNameStats,
}: NAVStatisticsProps) => {
  const chartData: StatisticsType[] = useMemo(() => {
    return getPieChartDataFromStatistics(byCarrierNameStats)
  }, [byCarrierNameStats])

  const carrierChartConfig: ChartConfig = useMemo(
    () =>
      Object.entries(byCarrierNameStats || {}).sort((a, b) => b[1] - a[1]).reduce(
        (acc, [carrierName], index) => {
          // Normalize carrier names to snake_case and ensure fallback carrier names for non-medical
          // enrollments are properly displayed (dental + vision).
          switch (carrierName) {
            case 'vsp':
              return {
                vsp: {
                  label: 'VSP',
                  color: chartColors[index || 0],
                },
                ...acc,
              }
            case 'guardian_dental':
              return {
                guardian_dental: {
                  label: 'Guardian',
                  color: chartColors[index || 0],
                },
                ...acc,
              }
            case 'renaissance_dental':
              return {
                renaissance_dental: {
                  label: 'Renaissance',
                  color: chartColors[index || 0],
                },
                ...acc,
              }
            case 'bsca_dental':
              return {
                bsca_dental: {
                  label: 'BlueShield of California',
                  color: chartColors[index || 0],
                },
                ...acc,
              }
            case 'bsca_vision':
              return {
                bsca_vision: {
                  label: 'BlueShield of California',
                  color: chartColors[index || 0],
                },
                ...acc,
              }
            case 'medicare':
              return {
                medicare: {
                  label: 'Medicare',
                  color: chartColors[index || 0],
                },
                ...acc,
              }
            case 'off_thatch_medical':
              return {
                off_thatch_medical: {
                  label: 'Off-Thatch Medical',
                  color: chartColors[index || 0],
                },
                ...acc,
              }
            case 'off_thatch_dental':
              return {
                off_thatch_dental: {
                  label: 'Off-Thatch Dental',
                  color: chartColors[index || 0],
                },
                ...acc,
              }
            case 'off_thatch_vision':
              return {
                off_thatch_vision: {
                  label: 'Off-Thatch Vision',
                  color: chartColors[index || 0],
                },
                ...acc,
              }
            default:
              return {
                [toSnakeCase(carrierName)]: {
                  label: carrierName,
                  color: chartColors[index || 0],
                },
                ...acc,
              }
          }
        },
        {} as ChartConfig
      ),
    [byCarrierNameStats, colors, chartColors]
  )

  return (
    <NeedsAttentionPieChart
      title='Carrier'
      nameKey='carrier'
      dataKey='items'
      chartData={chartData}
      chartConfig={{ ...baseChartConfig, ...carrierChartConfig }}
    />
  )
}

const columnHelper = createColumnHelper<StatisticsType>()

const columns = [
  columnHelper.accessor('carrier', {
    header: 'Carrier',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('items', {
    header: () => <div className='text-end'>Count</div>,
    cell: (info) => <div className='text-end'>{info.getValue()}</div>,
  }),
]

export const NAVCarrierNameTable = ({
  stats: byCarrierNameStats,
}: NAVStatisticsProps) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 7,
  })

  const chartData: StatisticsType[] = useMemo(() => {
    return getTableChartDataFromStatistics(byCarrierNameStats)
  }, [byCarrierNameStats])

  const table = useReactTable({
    data: chartData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: { pagination },
  })

  return (
    <div>
      <div className='flex items-center justify-center mb-4'>
        <div className='font-semibold'>Carrier</div>
      </div>
      <TanstackTable
        table={table}
        paginationLabelSingular='item'
        onNextPageClick={() =>
          setPagination((old) => ({ ...old, pageIndex: old.pageIndex + 1 }))}
        onPreviousPageClick={() =>
          setPagination((old) => ({ ...old, pageIndex: old.pageIndex - 1 }))}
        showPageSelectionIfSinglePage={false}
      />
    </div>
  )
}
